// Connection tier (TAS-398).
//
// Prod 4458 (19 Sep): a table on slow wifi waited on assets the room could not
// afford. The reveal costs about 5.6 MB for the first wine with everything on —
// a 2.7 MB film, a 0.9 MB bottle model, a 1.5 MB lighting map and the 3D
// runtime — and about 3.6 MB for each wine after. On a connection doing 50 KB/s
// that is a minute of nothing.
//
// perfTier answers "can this DEVICE afford the full show?" and memoizes forever,
// which is right for hardware. This module answers "can this CONNECTION afford
// it right now?", which is a different question with a different shape: it
// changes during the evening, it cannot be read reliably off the browser, and it
// has to be allowed to change its mind.
//
// ── Why it is measured, not asked ────────────────────────────────────────────
// navigator.connection does not exist on iOS Safari, which is most of a table.
// So the tier is taken from how long this game's OWN downloads actually took,
// and navigator.connection is only a hint used before any sample exists.
//
// Three sources, because no single one can see everything:
//
//   1. Resource timing, for SAME-ORIGIN assets: the game's own files, which
//      since 18 Oct 2026 include the bottle models (/models) as well as the
//      lighting map. It cannot see the films and the photographs: Supabase
//      storage serves them with `Access-Control-Allow-Origin: *` but NO
//      `Timing-Allow-Origin`, so the browser reports transferSize 0 for every
//      one of them (Codex; confirmed against the live headers). Permission to
//      READ a resource is not permission to TIME it.
//   2. Explicit samples from fetches this app performs itself — the bottle
//      model, its lighting map, and the lobby's warm-ups. Our own clock and our
//      own byte count, so the CORS masking does not apply. A fetch the HTTP
//      cache answered is not a sample (recordFetchSample, TAS-526).
//   3. A one-off probe of a same-origin asset when neither of the above has
//      produced anything, so a warm cache and a quiet start cannot leave the
//      tier guessing all evening.
//
// ── Tiers ────────────────────────────────────────────────────────────────────
//   full    the film and the 3D bottle (reveal + tasting notes)
//   middle  the film, and the bottle photo in place of the model
//   slow    the bottle photo immediately: no film, no model
//
// Override for testing, matching perfTier's convention:
//   localStorage.tasted_net = 'full' | 'middle' | 'slow'   (or ?net= in the URL)

export const NET_FULL = 'full';
export const NET_MIDDLE = 'middle';
export const NET_SLOW = 'slow';

const ORDER = { [NET_SLOW]: 0, [NET_MIDDLE]: 1, [NET_FULL]: 2 };
const isTier = (t) => Object.hasOwn(ORDER, t);

/** The lower (more cautious) of two tiers. */
export function worseTier(a, b) {
  if (!isTier(a)) return b;
  if (!isTier(b)) return a;
  return ORDER[a] <= ORDER[b] ? a : b;
}

// Throughput boundaries, in kilobits per second of OBSERVED transfer, set
// against what the reveal actually weighs.
//
// FULL asks for the first wine's whole bundle — 2.7 MB of film, a 0.9 MB model,
// the 1.5 MB lighting map and the 3D runtime, about 5.6 MB — inside the time
// the room spends between the curtain and the bottle. At 2500 kbps that is
// roughly eighteen seconds, with the model arriving while the film plays.
//
// MIDDLE keeps the film, and a film is watched while it downloads: the pipe has
// to outpace playback or it stalls halfway through the reveal. A 2.7 MB clip at
// 1000 kbps takes about twenty-two seconds, which a fifteen-second film just
// about survives with the get-ready beat in front of it. Below that it does
// not, which is why below that there is no film at all (Codex was right that
// the old 400 floor let through a fifty-four-second download).
const FULL_FLOOR_KBPS = 2500;
const MIDDLE_FLOOR_KBPS = 1000;

// Samples smaller than this say more about latency than bandwidth, and the
// mistake is asymmetric: 20 KB behind 200 ms of setup reads as 769 kbps on a
// 20 Mbit link, and we would take the 3D away from a table that could have had
// it (Codex). 50 KB over the same delay is a number worth believing.
// Only transfers big enough to be about bandwidth count. The compressed reveal
// films are 150–300 KB (2026-09-25), and at that size the time is mostly the
// wait for the first byte, so a fast laptop read as 'middle' and never got the
// 3D bottle (Mel, 2026-09-27). Models (1.5–7 MB) and the HDR still count.
const MIN_SAMPLE_BYTES = 400_000;
const PROBE_MIN_BYTES = 50_000;

// Weight on the newest sample. High on purpose: the point is to notice the
// evening getting worse, not to average across it.
const EWMA_ALPHA = 0.5;

// A file the HTTP cache answered reads as a disk, not a pipe (TAS-526). When
// resource timing cannot say where a fetch was served from, a reading faster
// than this, or over sooner than this, is taken to be the cache. 50 Mbit/s is
// twenty times the full floor, so a venue link is never mistaken for one.
const CACHE_LIKE_KBPS = 50_000;
const CACHE_LIKE_MS = 30;

// Resource entries the browser keeps (250 by default), and how many of the
// newest are held back here when it is emptied (watchTimingBuffer).
const RESOURCE_BUFFER_SIZE = 1000;
const KEEP_ON_CLEAR = 50;

// A single bad sample drops the tier at once — a phone that is struggling
// should not be handed another 3 MB to prove it. Going back up takes agreement
// from two in a row, so one lucky cached response cannot restore the full show.
const UPGRADES_NEEDED = 2;

let ewmaKbps = null;
let sampleCount = 0;
let current = null;         // last tier reported to subscribers
let pendingUpgrade = null;  // tier seen on the way up, waiting for a second
let pendingUpgradeCount = 0;
let sessionCap = null;      // set from the session's features (host override)
let observer = null;
let observerStarted = false;
let probed = false;
let bigSamples = 0;         // samples from real assets, as opposed to the probe
let lastProbeAt = 0;
let probing = false;
let lastFetchSample = null; // what the newest recordFetchSample did with its reading
let bufferFullListener = null; // set once the resource timing buffer is watched
let keptEntries = [];       // the newest resource entries from before the last clear

const listeners = new Set();

function nav() {
  return typeof navigator !== 'undefined' ? navigator : null;
}

function readOverride() {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('tasted_net');
      if (isTier(stored)) return stored;
    }
  } catch { /* storage blocked */ }
  try {
    if (typeof window !== 'undefined' && window.location?.search) {
      const fromUrl = new URLSearchParams(window.location.search).get('net');
      if (isTier(fromUrl)) return fromUrl;
    }
  } catch { /* malformed URL */ }
  return null;
}

/** The browser's own guess, used only until a real sample arrives. */
function hintedTier() {
  const conn = nav()?.connection;
  if (!conn) return null; // iOS Safari — measurement is the only source
  if (conn.saveData) return NET_SLOW; // the player asked for less data
  const effective = conn.effectiveType;
  if (effective === 'slow-2g' || effective === '2g') return NET_SLOW;
  if (effective === '3g') return NET_MIDDLE;
  if (effective === '4g') {
    // effectiveType saturates at '4g', so a genuinely fast link and a
    // congested one look identical here. downlink separates them when present.
    if (typeof conn.downlink === 'number' && conn.downlink > 0 && conn.downlink < 1.5) return NET_MIDDLE;
    return NET_FULL;
  }
  return null;
}

/**
 * The player asked the browser for less data (Data Saver). That holds all
 * evening, like the host's cap: a fast reading must not hand them the film and
 * the 3D bottle they opted out of (TAS-526).
 */
function saveDataCap() {
  return nav()?.connection?.saveData ? NET_SLOW : null;
}

function tierForKbps(kbps) {
  if (kbps >= FULL_FLOOR_KBPS) return NET_FULL;
  if (kbps >= MIDDLE_FLOOR_KBPS) return NET_MIDDLE;
  return NET_SLOW;
}

/** What the evidence says, before the session cap and the override. */
function measuredTier() {
  if (ewmaKbps !== null) return tierForKbps(ewmaKbps);
  return hintedTier() ?? NET_FULL; // nothing known yet: start generous, degrade fast
}

function emit(tier) {
  for (const fn of listeners) {
    try { fn(tier); } catch { /* a listener must not break the others */ }
  }
}

/**
 * The tier to render at right now.
 * Order of authority: manual override, then the caps (the session cap the host
 * set, and the player's Data Saver), then what the connection has actually done.
 */
export function netTier() {
  const override = readOverride();
  if (override) return override;
  startObserver();
  const measured = current ?? measuredTier();
  return worseTier(worseTier(measured, sessionCap), saveDataCap());
}

/** True when this connection can afford the 3D bottle and its lighting map. */
export function canAfford3D() {
  return netTier() === NET_FULL;
}

/** True when this connection can afford the reveal film. */
export function canAffordVideo() {
  return netTier() !== NET_SLOW;
}

/**
 * Fold one observed transfer into the estimate.
 *
 * `provisional` marks the probe: a 200 KB reading taken because nothing else
 * had been measured. It is enough to decide the first wine, but it is not
 * evidence to hold the evening to. Until a real asset has been measured, each
 * newer reading (a re-probe, or the first real asset) replaces the one before
 * it outright, in either direction, instead of being averaged with it or made
 * to agree twice (prod 2476, 2026-10-02: phones on decent wifi never got the
 * 3D bottle). Once real assets have spoken, the ordinary rules apply.
 * @param {{bytes: number, ms: number, minBytes?: number, provisional?: boolean}} sample
 */
export function recordAssetSample({ bytes, ms, minBytes = MIN_SAMPLE_BYTES, provisional = false } = {}) {
  if (!Number.isFinite(bytes) || !Number.isFinite(ms)) return;
  if (bytes < minBytes || ms <= 0) return;

  // Read where we stood BEFORE folding this sample in — measuredTier() is
  // computed from the estimate, so taking it afterwards compares the new
  // reading against itself and nothing ever changes.
  //
  // Committing it to `current` here matters as much: while `current` stayed
  // null, netTier() kept deriving a fresh answer from the moving estimate, so
  // the getter could already say 'full' during a staged upgrade that had not
  // notified anyone — subscribers on 'slow', direct callers on 'full' (Codex).
  const previous = current ?? measuredTier();
  current = previous;

  const kbps = (bytes * 8) / ms; // bytes/ms * 8 === kilobits/second
  const onlyProbeSoFar = bigSamples === 0 && sampleCount > 0;
  ewmaKbps = ewmaKbps === null || onlyProbeSoFar ? kbps : (EWMA_ALPHA * kbps) + ((1 - EWMA_ALPHA) * ewmaKbps);
  sampleCount += 1;
  if (!provisional) bigSamples += 1;

  const next = tierForKbps(ewmaKbps);
  if (onlyProbeSoFar && next !== previous) {
    // A newer reading overrules the probe's guess at once.
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    current = next;
    emit(netTier());
    return;
  }
  if (next === previous) {
    current = next;
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    return;
  }

  if (ORDER[next] < ORDER[previous]) {
    // Down: immediately.
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    current = next;
    emit(netTier());
    return;
  }

  // Up: only on agreement.
  if (pendingUpgrade === next) pendingUpgradeCount += 1;
  else { pendingUpgrade = next; pendingUpgradeCount = 1; }
  if (pendingUpgradeCount >= UPGRADES_NEEDED) {
    pendingUpgrade = null;
    pendingUpgradeCount = 0;
    current = next;
    emit(netTier());
  }
}

/**
 * Where resource timing says this fetch was served from: 'cache', 'network',
 * or null when it cannot say. Same-origin files (the models and the lighting
 * map) are fully timed; storage files are masked (see the header) and give
 * null, as does a browser that has not filed the entry.
 */
function servedFrom(url, startedAt) {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') return null;
  let name;
  try {
    name = new URL(url, globalThis.location?.href).href;
  } catch {
    return null;
  }
  // This fetch's own entry is the first one that began when it did or after.
  // An earlier entry describes an earlier download of the same file (it is
  // fetched again for every wine). A later one can be another fetch of it that
  // started while this one was under way and finished first, often from the
  // cache, so it must not speak for this download.
  const entry = [
    ...keptEntries.filter((e) => e.name === name),
    ...performance.getEntriesByName(name, 'resource'),
  ]
    .filter((e) => e.startTime >= startedAt - 1)
    .sort((a, b) => a.startTime - b.startTime)[0];
  if (!entry) return null;
  const { transferSize, encodedBodySize } = entry;
  // A masked entry zeroes both sizes; an old browser has no transferSize.
  if (typeof transferSize !== 'number' || !(encodedBodySize > 0)) return null;
  // Over the network the transfer is the headers plus the whole body. Less than
  // the body means the body came off the disk: 0 for a fresh cached copy, a
  // few hundred bytes of headers for a 304 that confirmed one.
  return transferSize < encodedBodySize ? 'cache' : 'network';
}

/**
 * A reading from a file this app fetched itself, which the HTTP cache may have
 * answered (TAS-526). The lighting map is a Vite asset cached for a year and
 * the reveal fetches the same model and map again for every wine, so these
 * readings were often a disk read at hundreds of thousands of kbps. One of
 * those held the tier at full after the venue wifi had gone, and the films and
 * the 3D bottle kept downloading.
 *
 * Resource timing decides when it can. When it cannot, a reading that only a
 * cache could produce is dropped. Returns what was done with the reading, which
 * netTierDebug() also reports:
 *   'used'      folded into the estimate
 *   'cache'     resource timing says the cache served it
 *   'too-fast'  no timing entry, and faster or quicker than a network would be
 *   'too-small' too few bytes to say anything about bandwidth
 * @param {{url?: string, bytes: number, ms: number, startedAt?: number}} sample
 *   `startedAt` is the performance.now() taken just before the fetch.
 */
export function recordFetchSample({ url, bytes, ms, startedAt = 0 } = {}) {
  const served = url ? servedFrom(url, startedAt) : null;
  let outcome;
  if (served === 'cache') {
    outcome = 'cache';
  } else if (served !== 'network' && (ms < CACHE_LIKE_MS || (bytes * 8) / ms > CACHE_LIKE_KBPS)) {
    outcome = 'too-fast';
  } else {
    const before = sampleCount;
    recordAssetSample({ bytes, ms });
    outcome = sampleCount > before ? 'used' : 'too-small';
  }
  lastFetchSample = outcome;
  return outcome;
}

/**
 * Keep the resource timing buffer from filling up for good. Browsers keep 250
 * entries, and a game fills that in about half an hour (presence, polls, RPCs,
 * analytics, taunt clips). After that servedFrom found nothing, and for the
 * rest of the evening a cached read was judged by the speed rule alone. So the
 * buffer is made bigger, and emptied whenever it fills anyway. The newest
 * entries are held back first: a fetch that has just finished may not have
 * been read yet. The observer in startObserver is not affected, because it is
 * handed every entry whether the buffer has room or not.
 */
function watchTimingBuffer() {
  if (bufferFullListener || typeof performance === 'undefined') return;
  bufferFullListener = () => {
    try {
      keptEntries = (performance.getEntriesByType?.('resource') ?? []).slice(-KEEP_ON_CLEAR);
      performance.clearResourceTimings?.();
    } catch { /* nothing to clear */ }
  };
  try {
    performance.setResourceTimingBufferSize?.(RESOURCE_BUFFER_SIZE);
    performance.addEventListener?.('resourcetimingbufferfull', bufferFullListener);
  } catch { /* an old browser keeps its default buffer */ }
}

/**
 * Watch every asset the game downloads, so the tier is informed by real traffic
 * rather than a probe we invented. Safe to call repeatedly.
 */
export function startObserver() {
  if (observerStarted) return;
  observerStarted = true;
  watchTimingBuffer();
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // transferSize is 0 for a cached response and for a cross-origin
        // response without Timing-Allow-Origin — neither says anything about
        // the pipe, so neither becomes a sample.
        const bytes = entry.transferSize;
        if (!bytes) continue;
        // responseStart → responseEnd is the body coming down the wire.
        // `duration` also carries queueing, DNS and TLS, which belong to the
        // first byte and must not be extrapolated across a 3 MB film.
        const transferMs = entry.responseEnd - entry.responseStart;
        recordAssetSample({ bytes, ms: transferMs > 0 ? transferMs : entry.duration });
      }
    });
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    observer = null; // resource timing unavailable — hints and explicit samples still work
  }
}

/** Subscribe to tier changes. Returns an unsubscribe. */
export function onNetTierChange(fn) {
  listeners.add(fn);
  startObserver();
  return () => listeners.delete(fn);
}

/**
 * A ceiling set by the session (features.assetTier), for a venue the host
 * already knows is rough. It can only make the evening lighter, never heavier:
 * a host cannot force 3D onto a phone that measured slow.
 */
export function setSessionTierCap(tier) {
  const next = isTier(tier) ? tier : null;
  if (next === sessionCap) return;
  sessionCap = next;
  emit(netTier());
}

/** For diagnostics and the tier badge in dev. */
export function netTierDebug() {
  return {
    tier: netTier(),
    measured: current ?? measuredTier(),
    kbps: ewmaKbps === null ? null : Math.round(ewmaKbps),
    samples: sampleCount,
    bigSamples,
    hint: hintedTier(),
    sessionCap,
    saveData: saveDataCap() !== null,
    // What the newest reading from our own fetches did: 'used', 'cache',
    // 'too-fast', 'too-small', or null before the first one (recordFetchSample).
    lastFetchSample,
    override: readOverride(),
  };
}

/**
 * Measure the connection against a real file, once, when nothing else has.
 *
 * Needed because the heavy assets are invisible to resource timing (see the
 * header note above) and a returning player's bundle comes off the disk cache,
 * so a table can reach the first wine with no evidence at all. Ranged so it
 * costs a fixed ~200 KB whatever the file is, and cache-busted so it measures
 * the network rather than the disk.
 *
 * @param {string} url  a SAME-ORIGIN asset (no CORS preflight, no masking)
 */
export async function probeConnection(url = '/hero-tasting.mp4', { bytes = 200_000, force = false } = {}) {
  if (probed && !force) return;
  probed = true;
  if (sampleCount > 0 && !force) return; // real traffic already told us
  if (typeof fetch !== 'function') return;
  if (probing) return;
  probing = true;
  lastProbeAt = Date.now();
  try {
    const started = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}netprobe=${started.toFixed(0)}`, {
      headers: { Range: `bytes=0-${bytes - 1}` },
      cache: 'no-store',
    });
    if (!response.ok) return;
    const body = await response.blob();
    const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started;
    // A server that ignores Range hands back the whole file; body.size is what
    // actually crossed the wire either way.
    // The probe is a deliberate, cache-busting measurement of a fixed size,
    // so it keeps the old floor rather than the bandwidth-only one.
    recordAssetSample({ bytes: body.size, ms: elapsed, minBytes: PROBE_MIN_BYTES, provisional: true });
  } catch {
    // Offline, blocked, or the asset moved. The hint and later samples stand.
  } finally {
    probing = false;
  }
}

// A tier below full stops the game downloading the very things it measures
// (no model, no lighting map), so one slow reading used to stand all evening.
// Measure again at each wine, a little bigger than the first probe so latency
// counts for less, as long as the connection is not plainly slow.
const REPROBE_GAP_MS = 60_000;
const REPROBE_BYTES = 300_000;

/**
 * Re-measure a connection the game has given up on, so a dip costs one wine
 * rather than the night. No-op when the tier is full, forced by the host or
 * the player, measured slow (300 KB there is not free), or measured recently.
 */
export function reprobeIfBelowFull(url) {
  if (readOverride()) return null;
  if (sessionCap && sessionCap !== NET_FULL) return null; // a lighter evening by choice
  if (saveDataCap()) return null; // the player's choice, and a probe is data
  const measured = current ?? measuredTier();
  if (measured !== NET_MIDDLE) return null;
  if (Date.now() - lastProbeAt < REPROBE_GAP_MS) return null;
  // Resolves when the reading is in, so a caller can act on the new tier.
  return probeConnection(url, { bytes: REPROBE_BYTES, force: true });
}

/** Test hook — forget everything that was measured. */
export function __resetNetTierForTests() {
  ewmaKbps = null;
  sampleCount = 0;
  current = null;
  pendingUpgrade = null;
  pendingUpgradeCount = 0;
  sessionCap = null;
  listeners.clear();
  try { observer?.disconnect(); } catch { /* already gone */ }
  observer = null;
  observerStarted = false;
  probed = false;
  bigSamples = 0;
  lastProbeAt = 0;
  probing = false;
  lastFetchSample = null;
  try { performance.removeEventListener?.('resourcetimingbufferfull', bufferFullListener); } catch { /* never added */ }
  bufferFullListener = null;
  keptEntries = [];
}
